import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, FormViewService, ProviderAccreditationPendenciesService, ToastService } from '@philips-emr/hpms-provider-commons';
import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent, HPMSOnCreateMenu, HPMSOnOptionClick } from '@philips-emr/hpms-provider-components';
import { FetchAPI, FieldAPI, FormAPI, LegendsAPI, PanelAPI, ToastNotificationAPI, Token } from '@philips-emr/tws-components';
import { ToastLevel, Type } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnCancel, OnChange, OnDynamicContentRender, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import './accreditation.css';

import { AccreditationSpecialtyAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import { AccreditationSteps } from './accreditation-update-controller';
import { ActionTypes } from './update-accreditation-type';

type MedicalSpecialty = {
  nrSequence?: number,
  ieMainSpecialty?: string,
  fieldOfWork?: number,
  nrFieldOfWork?: number,
  dsFieldOfWork?: string,
  specialty?: number,
  cdSpecialty?: number,
  dsAction: string;
  ieAction: ActionTypes;
};

const OptionKey = {
  EDIT: 'edit',
  DELETE: 'delete',
};

@Controller({ fragment: FragmentNames.ACCREDITATION_SPECIALTY })
class AccreditationSpecialtyController extends BaseController {
  @Inject(Aliases.FETCH_FIELD_OF_WORK)
  private fetchFieldOfWork: FetchAPI;
  @Inject(Aliases.FIELD_FIELD_OF_WORK)
  private fieldOfWork: FieldAPI;
  @Inject(Aliases.FORM_SPECIALTY)
  private formSpecialty: FormAPI;
  @Inject(Aliases.FETCH_LIST_SPECIALTY)
  private fetchListSpecialty: FetchAPI;
  @Inject(Aliases.PANEL_PENDENCIES)
  private panelPendencies: PanelAPI;
  @Inject(Aliases.TOAST_NOTIFICATION)
  private toastNotificationPendencies: ToastNotificationAPI;
  @Inject(Aliases.LEGENDS)
  private legends: LegendsAPI;

  @OnAfterFetch(Aliases.FETCH_LIST_SPECIALTY)
  onAfterFetchListSpecialty(event: FetchEvent): void {
    const { data } = event;
    if (isEmptyArrayOrObject(data)) {
      this.legends.setShowExpression('');
      this.legends.setHideExpression('');
    } else {
      this.legends.setShowExpression(MessageService.translate('tws.legends.show'));
      this.legends.setHideExpression(MessageService.translate('tws.legends.hide'));
    }
  }

  @OnMount(Aliases.LAYOUT_GENERAL_INFORMATION)
  onMountLayoutGeneralInformation(): void {
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqProvider, nrSeqAccreditation });
    this.fetchListSpecialty.forceFetch();
  }

  @OnMount(Aliases.FORM_PANEL)
  onMountForm(event: MountEvent): void {
    const api = event.target as PanelAPI;
    FormViewService.shouldRenderForm(api, AccreditationSteps.SPECIALTIES);
  }

  @OnMount(Aliases.FETCH_SPECIALTIES)
  onMountFetchSpecialties(event: MountEvent): void {
    const { nrSeqTypeAccreditation } = this.historyService.getCurrentRouteParams();
    const api = event.target as FetchAPI;
    api.setParameters({ nrSeqTypeAccreditation });
    api.forceFetch();
  }

  @OnMount(Aliases.FETCH_PENDENCIES)
  onMountFetch(event: MountEvent): void {
    ProviderAccreditationPendenciesService.fetchCardPendencies(event, AccreditationSteps.SPECIALTIES);
  }

  @OnAfterFetch(Aliases.FETCH_PENDENCIES)
  onAfterFetch(event: FetchEvent): void {
    if (isEmptyArrayOrObject(event.data)) {
      this.panelPendencies.setVisible(false);
    } else {
      ProviderAccreditationPendenciesService.setMessageToastPendency(this.toastNotificationPendencies, event);
    }
  }

  @OnChange(Aliases.FIELD_SPECIALTY)
  onChangeSpecialty(event: ChangeEvent<string>): void {
    const specialtyCode = event.value;
    this.context.set({ specialtyCode });
    if (!specialtyCode) {
      this.fieldOfWork.setValue(undefined);
    }
    this.fetchFieldOfWork.forceFetch();
  }

  @OnCancel(Aliases.FORM_SPECIALTY)
  onCancelForm(): void {
    this.formSpecialty.setRecord({});
  }

  @OnSubmit(Aliases.FORM_SPECIALTY)
  async onSubmitForm(): Promise<void> {
    const record = this.formSpecialty.getRecord() as MedicalSpecialty;
    await this.saveRecord(record);
  }

  private async saveRecord(record: MedicalSpecialty): Promise<void> {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    return HttpService.post(Endpoint.POST.ESTABLISHMENT_SPECIALTY, { nrSeqProvider, nrSeqAccreditation }, { body: { ...record } })
      .then(() => {
        this.formSpecialty.setRecord({});
        this.fetchListSpecialty.forceFetch();
      }).catch((response) => {
        ToastService.open(response);
      });
  }

  @OnMount(Aliases.LAYOUT_GENERAL_INFORMATION)
  onRootLayoutMount(): void {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    this.context.set({ nrSeqAccreditation, nrSeqProvider });
    this.fetchListSpecialty.forceFetch();
  }

  @HPMSOnOptionClick(Aliases.HPMS_CUSTOM_CARD_MEDICAL_SPECIALTY)
  onOptionClick(event: HpmsCustomCardEvent<Record<string, Value>>): void {
    const { data, key } = event;
    const { nrSequence } = data as MedicalSpecialty;

    const action = {
      [OptionKey.EDIT]: () => this.editRecord(Number(nrSequence)),
      [OptionKey.DELETE]: () => this.deleteRecord(Number(nrSequence)),
    };

    if (action[key]) {
      action[key]();
    }
  }

  private editRecord(nrSequence: number): void {
    this.formSpecialty.setRecord({});
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    HttpService.get<Record<string, Value>>(Endpoint.GET.ESTABLISHMENT_SPECIALTY, { nrSeqProvider, nrSeqAccreditation, nrSequence })
      .then((resp) => {
        const { body = {} } = resp;
        const specialtyCode = body.cdSpecialty;
        const nrFieldOfWork = body.nrFieldOfWork;
        this.context.set({ specialtyCode, nrFieldOfWork });
        this.formSpecialty.setData(body);
        this.formSpecialty.setRecord(body);
        this.fetchFieldOfWork.forceFetch();
        this.formSpecialty.validate();
      }).catch(() => ToastService.open({ message: MessageService.translate('provider.error-process-request'), level: ToastLevel.ERROR }));
  }

  private async deleteRecord(nrSequence: number): Promise<void> {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    await HttpService.delete(Endpoint.DELETE.ESTABLISHMENT_SPECIALTY, { nrSeqProvider, nrSeqAccreditation, nrSequence });
    this.formSpecialty.setRecord({});
    this.fetchListSpecialty.forceFetch();
  }

  @HPMSOnCreateMenu(Aliases.HPMS_CUSTOM_CARD_MEDICAL_SPECIALTY)
  onCreateCardMenu(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, shouldRender } = event;
    const { editable } = data;
    shouldRender(editable == 'S');
  }

  @OnDynamicContentRender(Aliases.DYNAMIC_FIELD_MAIN_SPECIALTY)
  public onDynamicContentRenderField(event: RenderEvent): void {
    const { data, render } = event;
    const { ieMainSpecialty } = data as MedicalSpecialty;

    if (ieMainSpecialty === 'S') {
      render((): ReactElement => this.getToken());
    }
  }

  private getToken(): ReactElement {
    return <div className="u-token-list-card">
      <Token value="provider.main" type={Type.NOTICE} />
    </div>;
  }
}

export {
  AccreditationSpecialtyController,
};
